import type { DrugTestResolution, DrugTestSource } from './types';

/** Matches checks_service WITHDRAWN_RESOLUTION_STATUS (banner lookup). */
export const WITHDRAWN_STATUSES = [
  'Collection Cancelled',
  'Deleted',
  'Expired, No Show',
  'Expired, Not Scheduled',
  'Refusal to Test',
  'Registration Expired',
] as const;

export type WithdrawnStatus = (typeof WITHDRAWN_STATUSES)[number];

export const SCHEDULING_ERROR_STATUS = 'Scheduling Error';

export const OPEN_DRUG_TEST_MODAL_PARAM = 'openDrugTestModal';

export const SOURCE_BADGE_LABEL: Record<DrugTestSource, string> = {
  bgc_screening: 'BGC',
  candidate_details: 'Dashboard',
  api_single: 'API',
};

/** Glance “Source” human prefix · enum */
export const SOURCE_GLANCE_LABEL: Record<DrugTestSource, string> = {
  bgc_screening: 'Background Check',
  candidate_details: 'A La Carte Dashboard',
  api_single: 'A La Carte API',
};

/** Selector / general display mapping. */
export const RESOLUTION_DISPLAY_LABEL: Record<DrugTestResolution, string> = {
  clear: 'clear · negative',
  consider: 'consider · positive',
  processing: 'processing',
  withdrawn: 'withdrawn',
};

/** Report selector resolution pill (uppercase short label). */
export const RESOLUTION_BADGE_LABEL: Record<DrugTestResolution, string> = {
  clear: 'CLEAR',
  consider: 'CONSIDER',
  processing: 'PROCESSING',
  withdrawn: 'WITHDRAWN',
};

export const GLANCE_RESOLUTION_LABEL: Record<DrugTestResolution, string> = {
  clear: 'clear · negative',
  consider: 'consider',
  processing: 'processing',
  withdrawn: 'withdrawn',
};

export const EMPTY_VALUE = '—';

export const EXPIRATION_WARNING_DAYS = 3;

export const WEBHOOK_EMPTY_MESSAGE = 'No webhook events linked to this report yet';
export const WEBHOOK_PANEL_TITLE = 'Webhook events';
export const WEBHOOK_LINKED_EMPTY_SUFFIX = 'assignment pending';
export const WEBHOOK_LINKED_NONEMPTY_SUFFIX = 'target state';

export const HISTORY_EMPTY_MESSAGE = 'No status history for this report yet';
export const HISTORY_PANEL_TITLE = 'Status history';

export const SCHEDULING_ERROR_BANNER = {
  title: 'Scheduling Error',
  message: 'I3Screen order creation failed. checks_service retries every ~8 hours via scheduling_error_status_cron.',
  note: 'No scheduling URL or donor pass yet. Check partner drug_test_org_id / drug_test_location_id and candidate address.',
} as const;

export const WITHDRAWN_BANNER_TITLE_PREFIX = 'Withdrawn';

/**
 * StatusAlert message for each withdrawn I3Screen status.
 * Placeholders:
 * - `({expiration})` / `({expiration when set})` → formatted date when present, omitted otherwise
 * - `{case_number}` → report case number (or `unknown`)
 */
export const WITHDRAWN_BANNER_COPY: Record<WithdrawnStatus, string> = {
  'Expired, Not Scheduled':
    'Donor never completed scheduling before the expiration window closed ({expiration}). I3Screen moved the case to Expired, Not Scheduled; resolution set to withdrawn.',
  'Expired, No Show':
    'Donor scheduled but did not complete collection before the window closed ({expiration}). I3Screen moved the case to Expired, No Show; resolution set to withdrawn.',
  'Registration Expired':
    'Donor registration expired before the test could be completed ({expiration when set}). I3Screen moved the case to Registration Expired; resolution set to withdrawn.',
  'Collection Cancelled':
    'Collection was cancelled at the site (or by the collector/provider). I3Screen moved the case to Collection Cancelled; resolution set to withdrawn.',
  'Refusal to Test':
    'Donor refused to complete the drug test. I3Screen moved the case to Refusal to Test; resolution set to withdrawn.',
  Deleted:
    'Order was deleted on I3Screen (case {case_number}). No further provider progress expected; resolution set to withdrawn.',
};

/** Fallback when resolution is withdrawn but status is missing / unexpected. */
export const WITHDRAWN_GENERIC_MESSAGE =
  'This drug test was withdrawn at I3Screen. Review status history and webhooks for details; re-order via A La Carte if testing is still required.';

/** Report selector note labels **/
export const NO_CASE_NUMBER_LABEL = 'No case number yet';
export const A_LA_CARTE_NOTE = 'À la carte';
export const NATIVE_NOTE = 'Native';

export const VIOLET = '#7364db';
export const VIOLET_LIGHT = '#ede9fe';
export const VIOLET_TEXT = '#4338ca';
